import { useCallback, useState } from 'react';
import { forceUnlockAllSheetModals } from './useFullscreenSheetModal';

const CONTEXT_KEYS = ['projectNo', 'projectName', 'workDate', 'division', 'scopeId'];

function sameContext(prev, next) {
  if (!prev) return false;
  return CONTEXT_KEYS.every((key) => prev[key] === next[key]);
}

/**
 * Which of the four daily-log screens is open, plus the shared project/date context.
 * Crew stays mounted for the whole visit so Back can return to in-progress edits.
 * Production and Inventory stay mounted after the first open for the same reason.
 */
export function useDailyLogFlow() {
  const [step, setStep] = useState(null);
  const [context, setContext] = useState(null);
  const [mounted, setMounted] = useState({ production: false, inventory: false });

  const begin = useCallback(() => {
    setStep('crew');
    setContext(null);
    setMounted({ production: false, inventory: false });
  }, []);

  const close = useCallback(() => {
    setStep(null);
    setContext(null);
    setMounted({ production: false, inventory: false });
    forceUnlockAllSheetModals();
  }, []);

  const navigate = useCallback((nextStep, patch) => {
    if (patch) {
      setContext((prev) => {
        const next = { ...(prev || {}), ...patch };
        if (
          sameContext(prev, next) &&
          Boolean(prev.hasCrewProjectLabor) === Boolean(next.hasCrewProjectLabor)
        ) {
          return prev;
        }
        return next;
      });
    }
    setStep(nextStep);
    setMounted((prev) => ({
      production: prev.production || nextStep === 'production',
      inventory: prev.inventory || nextStep === 'inventory',
    }));
  }, []);

  return {
    step,
    context,
    begin,
    close,
    navigate,
    crewFlowActive: step != null,
    productionMounted: mounted.production,
    inventoryMounted: mounted.inventory,
    productionVisible: step === 'production',
    inventoryVisible: step === 'inventory',
  };
}
